import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  FlatList,
  Image,
  Modal,
  Pressable,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { showMessage } from 'react-native-flash-message';
import AmbientGlow from '../components/AmbientGlow';
import TonyteBackground from '../components/TonyteBackground';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { auth } from '../firebase';
import { deleteChatForUser, useChats, useProfile } from '../hooks/useProfile';
import { getSharedInterests } from '../hooks/useVibes';
import {
  getUserDisplayName,
  TONYTE_PROFILE_IMAGE,
  useUserProfiles,
  type PublicUserProfile,
} from '../utils/userProfiles';
import type { ChatItem, RootStackParamList } from '../types';
import { Colors, Radius, Typography } from '../theme';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';

interface PersonalVibe {
  chat: ChatItem;
  userId: string;
  profile: PublicUserProfile;
  name: string;
}

const normalize = (value: string | undefined) => (value || '').trim().toLowerCase();

const formatLastActive = (value: string | undefined, t: (key: string) => string) => {
  if (!value) return t('vibesList.recentlyActive');
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return t('vibesList.recentlyActive');
  const minutes = Math.max(1, Math.round((Date.now() - time) / 60000));
  if (minutes < 60) return t('vibesList.activeMinutes').replace('{count}', String(minutes));
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('vibesList.activeHours').replace('{count}', String(hours));
  return t('vibesList.activeDays').replace('{count}', String(Math.round(hours / 24)));
};

const ItemGap = () => <View style={{ height: 13 }} />;

const Avatar = ({ uri, verified }: { uri?: string; verified?: boolean }) => (
  <View style={styles.avatarWrap}>
    <Image
      source={uri ? { uri } : TONYTE_PROFILE_IMAGE}
      style={styles.avatar}
      resizeMode="cover"
    />
    <VibeVerifiedBadge verified={verified} size={62} />
  </View>
);

const VibeCard = React.memo(function VibeCard({
  vibe,
  index,
  entrance,
  onOpenProfile,
  onOpenChat,
  onRequestRemove,
}: {
  vibe: PersonalVibe;
  index: number;
  entrance: Animated.Value;
  onOpenProfile: (vibe: PersonalVibe) => void;
  onOpenChat: (vibe: PersonalVibe) => void;
  onRequestRemove: (vibe: PersonalVibe) => void;
}) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createVibesAppearanceStyles(theme), [theme]);
  const secondary = vibe.profile.bio || vibe.profile.city || vibe.profile.location || t('vibesList.community');
  const activity = vibe.chat.lastMsg || formatLastActive(vibe.profile.lastActiveAt, t);

  return (
    <Animated.View
      style={[
        styles.card,
        appearance.card,
        {
          opacity: entrance,
          transform: [{
            translateY: entrance.interpolate({
              inputRange: [0, 1],
              outputRange: [18 + Math.min(index, 4) * 4, 0],
            }),
          }],
        },
      ]}
    >
      <LinearGradient
        colors={theme.isDark
          ? ['rgba(255,255,255,0.085)', 'rgba(255,255,255,0.025)']
          : ['rgba(255,255,255,0.98)', 'rgba(255,250,242,0.72)']}
        style={StyleSheet.absoluteFill}
      />
      <TouchableOpacity onPress={() => onOpenProfile(vibe)} activeOpacity={0.82}>
        <Avatar uri={vibe.profile.profileImage} verified={vibe.profile.faceVerified === true} />
      </TouchableOpacity>

      <View style={styles.cardBody}>
        <Text style={[styles.name, appearance.textPrimary]} numberOfLines={1}>
          {vibe.name}{vibe.profile.age ? `, ${vibe.profile.age}` : ''}
        </Text>
        <Text style={[styles.secondary, appearance.textSecondary]} numberOfLines={1}>{secondary}</Text>
        <View style={styles.previewRow}>
          <Icon
            name={vibe.chat.lastMsg ? 'message-circle' : 'clock'}
            size={13}
            color={theme.colors.iconMuted}
          />
          <Text style={[styles.preview, appearance.textMuted]} numberOfLines={1}>{activity}</Text>
          {vibe.chat.time ? <Text style={[styles.time, appearance.time]}>{vibe.chat.time}</Text> : null}
        </View>

        <View style={styles.actions}>
          <TouchableOpacity style={styles.messageButton} onPress={() => onOpenChat(vibe)} activeOpacity={0.84}>
            <Icon name="message-circle" size={15} color="#fff" />
            <Text style={styles.messageButtonText}>{t('vibesList.message')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.profileButton, appearance.secondaryButton]} onPress={() => onOpenProfile(vibe)} activeOpacity={0.84}>
            <Text style={[styles.profileButtonText, appearance.secondaryButtonText]}>{t('vibesList.viewProfile')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.removeButton}
            onPress={() => onRequestRemove(vibe)}
            activeOpacity={0.84}
            accessibilityLabel={t('vibesList.removeAccessibility').replace('{name}', vibe.name)}
          >
            <Icon name="user-minus" size={16} color="#FB7185" />
          </TouchableOpacity>
        </View>
      </View>
    </Animated.View>
  );
});

export default function VibesScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createVibesAppearanceStyles(theme), [theme]);
  const userId = auth.currentUser?.uid ?? null;
  const { profile: ownProfile } = useProfile(userId);
  const { chats, loading } = useChats(userId);
  const [search, setSearch] = useState('');
  const [removeTarget, setRemoveTarget] = useState<PersonalVibe | null>(null);
  const [removing, setRemoving] = useState(false);
  const entrance = useRef(new Animated.Value(0)).current;

  const personalChats = useMemo(() => {
    if (!userId) return [];
    const seenUserIds = new Set<string>();

    return chats.flatMap(chat => {
      const isDirectChat = chat.kind === 'match' || chat.kind === 'vibe';
      const uniqueParticipants = Array.from(new Set(chat.participantIds.filter(Boolean)));
      const otherUserId = uniqueParticipants.find(id => id !== userId);

      if (
        !isDirectChat
        || uniqueParticipants.length !== 2
        || !uniqueParticipants.includes(userId)
        || !otherUserId
        || seenUserIds.has(otherUserId)
      ) {
        return [];
      }

      seenUserIds.add(otherUserId);
      return [{ chat, userId: otherUserId }];
    });
  }, [chats, userId]);

  const profiles = useUserProfiles(personalChats.map(item => item.userId));

  const vibes = useMemo<PersonalVibe[]>(() => personalChats.flatMap(item => {
    const profile = profiles[item.userId];
    if (!profile?.id) return [];
    return [{
      ...item,
      profile,
      name: getUserDisplayName(profile),
    }];
  }), [personalChats, profiles]);

  const filteredVibes = useMemo(() => {
    const term = normalize(search);
    if (!term) return vibes;

    return vibes.filter(vibe => [
      vibe.name,
      vibe.profile.username,
      vibe.profile.city,
      vibe.profile.location,
      vibe.profile.university,
    ].some(value => normalize(value).includes(term)));
  }, [search, vibes]);

  useEffect(() => {
    entrance.setValue(0);
    Animated.spring(entrance, {
      toValue: 1,
      useNativeDriver: true,
      speed: 18,
      bounciness: 4,
    }).start();
  }, [entrance, filteredVibes.length]);

  const openProfile = useCallback((vibe: PersonalVibe) => {
    navigation.navigate('PublicProfile', {
      userId: vibe.userId,
      name: vibe.name,
      chatId: vibe.chat.id,
      vibeStatus: 'connected',
      sharedInterests: getSharedInterests(ownProfile?.interests, vibe.profile.interests),
    });
  }, [navigation, ownProfile?.interests]);

  const openChat = useCallback((vibe: PersonalVibe) => {
    navigation.navigate('ChatDetail', {
      chatId: vibe.chat.id,
      name: vibe.name,
      kind: vibe.chat.kind,
      photo: vibe.profile.profileImage,
      participantIds: vibe.chat.participantIds,
    });
  }, [navigation]);

  const removeVibe = useCallback(async () => {
    if (!userId || !removeTarget || removing || !removeTarget.chat.kind) return;
    setRemoving(true);
    try {
      await deleteChatForUser({
        chatId: removeTarget.chat.id,
        kind: removeTarget.chat.kind,
        userId,
      });
      setRemoveTarget(null);
      showMessage({ message: t('vibesList.removeSuccess').replace('{name}', removeTarget.name), type: 'default' });
    } catch (error) {
      console.warn('[Tonyte][RN][Vibes] remove failed', {
        chatId: removeTarget.chat.id,
        kind: removeTarget.chat.kind,
        code: (error as { code?: string }).code,
        error,
      });
      showMessage({ message: t('vibesList.removeFailed'), type: 'danger' });
    } finally {
      setRemoving(false);
    }
  }, [removeTarget, removing, t, userId]);

  const renderVibe = useCallback(({ item, index }: { item: PersonalVibe; index: number }) => (
    <VibeCard
      vibe={item}
      index={index}
      entrance={entrance}
      onOpenProfile={openProfile}
      onOpenChat={openChat}
      onRequestRemove={setRemoveTarget}
    />
  ), [entrance, openProfile, openChat]);

  const keyExtractorVibe = useCallback((vibe: PersonalVibe) => vibe.userId, []);

  return (
    <TonyteBackground appearanceAware style={[styles.root, appearance.root]}>
      <StatusBar barStyle={theme.statusBarStyle} backgroundColor={theme.colors.background} />
      <AmbientGlow tone="orange" size={280} intensity={0.28} style={[styles.topGlow, appearance.ambient]} />
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.backButton, appearance.backButton]} onPress={() => navigation.goBack()} activeOpacity={0.82}>
          <Icon name="arrow-left" size={21} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.heading}>
          <Text style={styles.eyebrow}>{t('vibesList.eyebrow')}</Text>
          <Text style={[styles.title, appearance.textPrimary]}>{t('vibesList.title')}</Text>
        </View>
        <View style={styles.headerSpacer} />
      </View>

      <FlatList
        data={filteredVibes}
        keyExtractor={keyExtractorVibe}
        renderItem={renderVibe}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 40 }]}
        ItemSeparatorComponent={ItemGap}
        ListHeaderComponent={(
          <View style={[styles.searchShell, appearance.searchShell]}>
            <Icon name="search" size={19} color={theme.colors.iconMuted} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder={t('vibesList.search')}
              placeholderTextColor={theme.colors.textMuted}
              style={[styles.searchInput, appearance.textPrimary]}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="search"
            />
            {search ? (
              <Pressable onPress={() => setSearch('')} hitSlop={10}>
                <Icon name="x-circle" size={18} color={theme.colors.iconMuted} />
              </Pressable>
            ) : null}
          </View>
        )}
        ListEmptyComponent={loading ? (
          <ActivityIndicator color={Colors.sunset} style={styles.loader} />
        ) : search && vibes.length ? (
          <View style={[styles.emptyCard, appearance.emptyCard]}>
            <Icon name="search" size={26} color={Colors.sunset} />
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>{t('vibesList.noMatches')}</Text>
            <Text style={[styles.emptyText, appearance.textSecondary]}>{t('vibesList.noMatchesSub')}</Text>
          </View>
        ) : (
          <View style={[styles.emptyCard, appearance.emptyCard]}>
            <View style={styles.emptyIcon}>
              <Icon name="users" size={28} color={Colors.sunset} />
            </View>
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>{t('vibesList.empty')}</Text>
            <Text style={[styles.emptyText, appearance.textSecondary]}>
              {t('vibesList.emptySub')}
            </Text>
            <TouchableOpacity
              style={styles.dailyVibesButton}
              onPress={() => navigation.navigate('DayVibesQuestion')}
              activeOpacity={0.84}
            >
              <Icon name="zap" size={16} color="#fff" />
              <Text style={styles.dailyVibesButtonText}>{t('vibesList.openDailyVibes')}</Text>
            </TouchableOpacity>
          </View>
        )}
      />

      <Modal
        visible={Boolean(removeTarget)}
        transparent
        animationType="fade"
        onRequestClose={() => !removing && setRemoveTarget(null)}
      >
        <View style={[styles.modalBackdrop, appearance.modalBackdrop]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => !removing && setRemoveTarget(null)} />
          <View style={[styles.modalCard, appearance.modalCard]}>
            <View style={styles.modalIcon}>
              <Icon name="user-minus" size={23} color="#FB7185" />
            </View>
            <Text style={[styles.modalTitle, appearance.textPrimary]}>{t('vibesList.removeTitle')}</Text>
            <Text style={[styles.modalText, appearance.textSecondary]}>
              {t('vibesList.removeBody')}
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalCancel, appearance.secondaryButton]}
                disabled={removing}
                onPress={() => setRemoveTarget(null)}
              >
                <Text style={[styles.modalCancelText, appearance.secondaryButtonText]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalRemove} disabled={removing} onPress={removeVibe}>
                {removing
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.modalRemoveText}>{t('common.delete')}</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  topGlow: {
    position: 'absolute',
    top: -120,
    right: -100,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingBottom: 16,
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  heading: {
    flex: 1,
    alignItems: 'center',
  },
  eyebrow: {
    color: Colors.sunset,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 2.1,
  },
  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: Typography.extrabold,
    marginTop: 1,
  },
  headerSpacer: {
    width: 42,
  },
  content: {
    paddingHorizontal: 18,
  },
  searchShell: {
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingHorizontal: 16,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.20)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    marginBottom: 18,
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 15,
    paddingVertical: 0,
  },
  loader: {
    marginTop: 80,
  },
  card: {
    minHeight: 150,
    flexDirection: 'row',
    gap: 14,
    padding: 15,
    overflow: 'hidden',
    borderRadius: 23,
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.17)',
    backgroundColor: 'rgba(10,15,25,0.70)',
  },
  avatarWrap: { width: 62, height: 62 },
  avatar: {
    width: 62,
    height: 62,
    borderRadius: 31,
    borderWidth: 1.5,
    borderColor: 'rgba(255,122,0,0.46)',
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '800',
  },
  secondary: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 13,
    marginTop: 3,
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
  },
  preview: {
    flex: 1,
    color: 'rgba(255,255,255,0.46)',
    fontSize: 12,
  },
  time: {
    color: 'rgba(255,255,255,0.32)',
    fontSize: 11,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
  },
  messageButton: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 13,
    borderRadius: 12,
    backgroundColor: Colors.sunset,
  },
  messageButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '800',
  },
  profileButton: {
    height: 36,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.11)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  profileButtonText: {
    color: 'rgba(255,255,255,0.76)',
    fontSize: 12,
    fontWeight: '700',
  },
  removeButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(251,113,133,0.20)',
    backgroundColor: 'rgba(251,113,133,0.07)',
  },
  emptyCard: {
    alignItems: 'center',
    marginTop: 44,
    paddingHorizontal: 28,
    paddingVertical: 36,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.16)',
    backgroundColor: 'rgba(255,255,255,0.045)',
  },
  emptyIcon: {
    width: 58,
    height: 58,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 29,
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.28)',
    backgroundColor: 'rgba(255,122,0,0.10)',
  },
  emptyTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '800',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyText: {
    color: 'rgba(255,255,255,0.56)',
    fontSize: 14,
    lineHeight: 21,
    marginTop: 8,
    textAlign: 'center',
  },
  dailyVibesButton: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: 18,
    borderRadius: 14,
    backgroundColor: Colors.sunset,
    marginTop: 22,
  },
  dailyVibesButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '800',
  },
  modalBackdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: 'rgba(0,0,0,0.78)',
  },
  modalCard: {
    width: '100%',
    maxWidth: 390,
    alignItems: 'center',
    padding: 24,
    borderRadius: 26,
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.20)',
    backgroundColor: '#111723',
  },
  modalIcon: {
    width: 52,
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 26,
    backgroundColor: 'rgba(251,113,133,0.10)',
  },
  modalTitle: {
    color: '#fff',
    fontSize: 21,
    fontWeight: '800',
    marginTop: 16,
  },
  modalText: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    marginTop: 9,
  },
  modalActions: {
    width: '100%',
    flexDirection: 'row',
    gap: 10,
    marginTop: 24,
  },
  modalCancel: {
    flex: 1,
    height: 46,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  modalCancelText: {
    color: 'rgba(255,255,255,0.76)',
    fontWeight: '800',
  },
  modalRemove: {
    flex: 1,
    height: 46,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    backgroundColor: '#E05266',
  },
  modalRemoveText: {
    color: '#fff',
    fontWeight: '800',
  },
});

const createVibesAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  root: daylightStyle(theme, {
    backgroundColor: theme.colors.background,
  }),
  ambient: daylightStyle(theme, {
    opacity: 0.16,
  }),
  backButton: daylightStyle(theme, {
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surfaceElevated,
    ...theme.shadows.card,
  }),
  textPrimary: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  textSecondary: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  textMuted: daylightStyle(theme, {
    color: theme.colors.textMuted,
  }),
  time: daylightStyle(theme, {
    color: theme.colors.iconFaint,
  }),
  searchShell: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.24)',
    backgroundColor: theme.colors.inputBackground,
    ...theme.shadows.card,
  }),
  card: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.20)',
    backgroundColor: theme.colors.cardBackground,
    ...theme.shadows.card,
  }),
  secondaryButton: daylightStyle(theme, {
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
  }),
  secondaryButtonText: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  emptyCard: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.20)',
    backgroundColor: theme.colors.surfaceElevated,
    ...theme.shadows.card,
  }),
  modalBackdrop: daylightStyle(theme, {
    backgroundColor: 'rgba(48,38,28,0.42)',
  }),
  modalCard: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.24)',
    backgroundColor: theme.colors.surfaceElevated,
    ...theme.shadows.elevated,
  }),
});
